import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }

    if (!token) { setError('Missing verification token. Please use the link from your email.'); return; }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/complete-registration`,
        { token, password },
        { withCredentials: true }
      );
      setSuccess(true);
      // Cookie was set by the server, check auth
      await checkAuth();
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <Link to="/" className="inline-block">
              <img src={`${API_URL}/api/uploads/og_logo.png`} alt="OG MUSIC GROUP" className="h-16 mx-auto" />
            </Link>
          </div>
          <div className="glass-card p-8 text-center" data-testid="set-password-success">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Account Created!</h2>
            <p className="text-white/50 text-sm">Welcome to OG MUSIC GROUP. Redirecting to your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <img src={`${API_URL}/api/uploads/og_logo.png`} alt="OG MUSIC GROUP" className="h-16 mx-auto" />
          </Link>
        </div>
        <div className="glass-card p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold mb-2">Set Your Password</h1>
            <p className="text-white/50 text-sm">Choose a secure password to complete your registration.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="set-password-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full input-glass pr-12"
                  placeholder="Min. 6 characters"
                  required
                  data-testid="set-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full input-glass"
                placeholder="Repeat password"
                required
                data-testid="set-password-confirm"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-gold py-4 flex items-center justify-center gap-2"
              data-testid="set-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetPasswordPage;
